import type { Role } from "@/lib/api-client";
import type { Locale } from "@/lib/i18n";
import { translate } from "@/lib/i18n";

// Pass-through helpers that resolve labels via the dictionary. Callers
// pass the current locale so we look up the right language.

// App-wide display currency (ISO 4217). Default EUR; the admin can change it
// in the console (persisted in AppSetting). Bootstrap loads it from /api/settings.
let activeCurrency = "EUR";
export function setAppCurrency(currency: string): void {
  if (currency && currency.length === 3) activeCurrency = currency.toUpperCase();
}
export function getAppCurrency(): string {
  return activeCurrency;
}

export function formatPrice(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: activeCurrency,
    maximumFractionDigits: 0,
  }).format(n ?? 0);
}

export function formatDate(iso: string, locale: Locale = "en"): string {
  const d = new Date(iso);
  const map: Record<Locale, string> = { en: "en-US", fr: "fr-FR", de: "de-DE" };
  return d.toLocaleDateString(map[locale] || "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateShort(iso: string, locale: Locale = "en"): string {
  const d = new Date(iso);
  const map: Record<Locale, string> = { en: "en-US", fr: "fr-FR", de: "de-DE" };
  return d.toLocaleDateString(map[locale] || "en-US", {
    month: "short",
    day: "numeric",
  });
}

export function nightsBetween(startIso: string, endIso: string): number {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000)));
}

export const PROPERTY_TYPES = [
  "HOUSE",
  "APARTMENT",
  "VILLA",
  "CHALET",
  "STUDIO",
  "COTTAGE",
];

export function propertyTypeLabel(t: string, locale: Locale = "en"): string {
  return translate(locale, `property.${t}`);
}

export function bookingTypeLabel(t: string, locale: Locale = "en"): string {
  return translate(locale, `booking.${t}`);
}

export function bookingTypeShortLabel(t: string, locale: Locale = "en"): string {
  return translate(locale, `booking.${t}.short`);
}

export function bookingTypeBadgeClass(t: string): string {
  if (t === "SHARED")
    return "bg-violet-100 text-violet-800 border-violet-200";
  return "bg-sky-100 text-sky-800 border-sky-200";
}

export function roleLabel(role: Role | string, locale: Locale = "en"): string {
  return translate(locale, `role.${role}`);
}

export function roleBadgeClass(role: string): string {
  if (role === "ADMIN") return "bg-amber-100 text-amber-800 border-amber-200";
  if (role === "OWNER")
    return "bg-emerald-100 text-emerald-800 border-emerald-200";
  return "bg-sky-100 text-sky-800 border-sky-200";
}

export function statusLabel(s: string, locale: Locale = "en"): string {
  return translate(locale, `status.${s}`);
}

export function statusBadgeClass(s: string): string {
  if (s === "APPROVED") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "PENDING") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "REJECTED") return "bg-rose-100 text-rose-800 border-rose-200";
  return "bg-zinc-100 text-zinc-700 border-zinc-200";
}

export function amenityList(s: string): string[] {
  return (s || "").split(",").map((a) => a.trim()).filter(Boolean);
}

export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function nightLabel(count: number, locale: Locale = "en"): string {
  return translate(locale, count === 1 ? "night" : "nights");
}

export function guestLabel(count: number, locale: Locale = "en"): string {
  return translate(locale, "guests");
}

export function pluralPropertyLabel(count: number, locale: Locale = "en"): string {
  return translate(locale, count === 1 ? "browse.resultSingular" : "browse.resultPlural");
}

// Staff roles see the moderation tools (dashboard, expenses, calendar).
// Fine-grained abilities come from the permission table server-side.
export function isStaffRole(role?: string | null): boolean {
  return role === "ADMIN" || role === "OWNER" || role === "MODERATOR";
}

export function isOwnerAdmin(role?: string | null): boolean {
  return role === "ADMIN" || role === "OWNER";
}
